import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useBudget } from '../../context/BudgetContext';
import { calculatePayoffTimeline } from '../debt/debtCalculations';
import { formatCAD } from '../../utils/formatCurrency';

export default function DebtFreeDateCard() {
  const { t } = useTranslation();
  const { state, derived } = useBudget();

  if (!state.goals.hasDebt || state.debts.length === 0) return null;

  const extraPayment = Math.max(0, derived.monthlyNet * 0.1);
  const result = calculatePayoffTimeline(state.debts, state.debtStrategy, extraPayment);

  const monthsLeft = result.debtFreeMonth;
  const years = Math.floor(monthsLeft / 12);
  const months = monthsLeft % 12;

  return (
    <Card elevation={1} sx={{ height: '100%', border: '1px solid #E6E9EE', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
      <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.25, color: '#1E3A5F' }}>
          {t('budgetPlanner.goals.debtFree.title', 'Libre de deudas')}
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 36, color: '#1E3A5F', mb: 0.75 }} />
          <Typography variant="h4" fontWeight={700} color="#1E3A5F">
            {monthsLeft > 0 ? (
              <>
                {years > 0 && `${years} ${t('budgetPlanner.goals.debtFree.years', 'años')} `}
                {months > 0 && `${months} ${t('budgetPlanner.goals.debtFree.months', 'meses')}`}
              </>
            ) : (
              t('budgetPlanner.goals.debtFree.free', 'Sin deuda')
            )}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.goals.debtFree.totalDebt', 'Deuda total')}: {formatCAD(derived.totalDebt)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.goals.debtFree.totalInterest', 'Interés total')}: {formatCAD(result.totalInterest)}
        </Typography>

        {/* Micro-insight */}
        {monthsLeft > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
              Pagando {formatCAD(extraPayment)}/mes extra, podrías ser libre de deuda en{' '}
              {years > 0 ? `${years} años ` : ''}{months > 0 ? `${months} meses` : ''}.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
